/* ========================================
   INCRED GOLF — PDP CONFIGURATOR
   Pre-purchase build configurator for the product page.

   Aligned to the spec sheet. Sections, field order, on-screen labels and
   control types all come from IG_SPEC_RULES.sections / fieldLabels /
   fieldControls, which mirror sections/spec-sheet-form.liquid exactly. Option
   values come from IG_SPEC_CONFIG, lifted verbatim from spec-sheet.js.
   Change the spec sheet, change assets/spec-config.js, and both surfaces move
   together.

   Every answer is written as a Shopify LINE ITEM PROPERTY on /cart/add.js,
   keyed to the same names the worker writes into the Airtable Spec Sheets
   table. Nothing here creates a variant: the discrete spec space is ~3.8M
   combinations on the cheapest configuration of one putter, against Shopify's
   2,048-variant and 3-option ceilings. Putter Type x Customization Level stay
   variants because they carry all the pricing.
   ======================================== */

(function() {
  'use strict';

  var CFG = window.IG_SPEC_CONFIG;
  var RULES = window.IG_SPEC_RULES;
  if (!CFG || !RULES) return;

  var root = document.querySelector('[data-ig-configurator]');
  if (!root) return;

  var product = null;
  try {
    var dataEl = root.querySelector('[data-product-json]');
    if (dataEl) product = JSON.parse(dataEl.textContent);
  } catch (e) {}
  if (!product) return;

  var state = {
    step: 'basics',
    options: {},
    specs: {
      hand: RULES.defaults.hand,
      length: null,          /* set by applyPutterType() */
      lieAngle: RULES.defaults.lieAngle,
      weight: null,
      loft: RULES.defaults.loft,
      shaft: null,
      gripBrand: null,
      grip: null,
      headcover: null,
      faceMaterial: null,
      engraving: '',
      sightLines: null,
      faceAngle: RULES.defaults.faceAngle,
      paintColor: null,
      faceGrooves: null,
      notes: ''
    }
  };

  /* ── Helpers ── */
  function esc(str) {
    var d = document.createElement('div');
    d.textContent = str == null ? '' : String(str);
    return d.innerHTML;
  }

  function optionValue(name) {
    var keys = Object.keys(state.options);
    for (var i = 0; i < keys.length; i++) {
      if (keys[i].toLowerCase() === name) return state.options[keys[i]];
    }
    return null;
  }

  function putterType() {
    var v = optionValue('putter type');
    return v ? v.toLowerCase().replace(/\s+/g, '') : 'regular';
  }

  function tierKey() {
    var v = optionValue('customization level');
    return v ? v.toLowerCase() : 'custom';
  }

  function tier() { return CFG.tiers[tierKey()] || CFG.tiers.custom; }
  function variantSpec() { return CFG.variantSpecs[putterType()] || CFG.variantSpecs.regular; }
  function isBespoke() { return tierKey() === 'bespoke'; }

  function withUnit(specKey, value) {
    if (value == null || value === '') return value;
    return String(value) + (RULES.units[specKey] || '');
  }

  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: (cents % 100 === 0) ? 0 : 2,
      maximumFractionDigits: 2
    });
  }

  function track(event, payload) {
    if (typeof gtag !== 'function') return;
    payload = payload || {};
    payload.product_handle = product.handle;
    payload.spec_tier = optionValue('customization level') || '';
    payload.putter_type = optionValue('putter type') || '';
    gtag('event', event, payload);
  }

  /* ── Steps come from the spec sheet's own sections ──
     The Bespoke section is skipped for Custom and Premium, exactly as the spec
     sheet skips its bespoke screen. Notes rides on the last step, where the
     spec sheet puts it — on the review screen, right before submit. */
  /* The questions a step actually asks at the current tier. A tier can narrow
     the set -- Stock is ready-built, so it offers only the two things adjustable
     on a finished head -- and Bespoke extras are gated separately. */
  function tierFields(section) {
    var only = tier().fieldsOnly || null;
    return (section.fields || []).filter(function(k) {
      if (!isBespoke() && RULES.bespokeOnly.indexOf(k) !== -1) return false;
      return !only || only.indexOf(k) !== -1;
    });
  }

  function steps() {
    return RULES.sections.filter(function(s) {
      if (s.bespokeOnly && !isBespoke()) return false;
      /* Basics has no fields by design -- it carries the two priced Shopify
         options -- so it is never empty and must not be filtered out. */
      if (s.key === 'basics') return true;
      /* Everything else disappears when the tier asks nothing there, so Stock
         does not present an empty Components step. */
      return tierFields(s).length > 0;
    });
  }
  function stepIndex() {
    var list = steps();
    for (var i = 0; i < list.length; i++) if (list[i].key === state.step) return i;
    return 0;
  }

  /* ── Variants ── */
  function currentVariant() {
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i], match = true;
      for (var j = 0; j < product.options.length; j++) {
        if (state.options[product.options[j]] !== v.options[j]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  /* Not every tier spans every putter type — Stock is Regular-only — so a
     selection can land on a combination with no variant. The option just
     changed is authoritative; everything else moves to the first variant that
     honours it. Without this the price silently kept its previous value while
     the buyer looked at a putter that cannot be bought. */
  function snapToRealVariant(changedName) {
    if (currentVariant()) return;
    var want = state.options[changedName];
    var idx = product.options.indexOf(changedName);
    if (idx === -1) return;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].options[idx] === want) {
        var v = product.variants[i];
        product.options.forEach(function(n, j) { state.options[n] = v.options[j]; });
        return;
      }
    }
  }

  /* What the putter would cost with this one option value, others held steady.
     Both options move the price — Armlock and Broomstick add $200 at every
     tier — so the delta is read off the catalogue, never hardcoded. */
  function priceForOption(name, value) {
    var idx = product.options.indexOf(name);
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i], match = true;
      for (var j = 0; j < product.options.length; j++) {
        var want = (j === idx) ? value : state.options[product.options[j]];
        if (v.options[j] !== want) { match = false; break; }
      }
      if (match) return v.price;
    }
    return null;
  }

  function orderedOptions() {
    return product.options.slice().sort(function(a, b) {
      var ia = RULES.optionOrder.indexOf(a.toLowerCase());
      var ib = RULES.optionOrder.indexOf(b.toLowerCase());
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }

  /* ── Values per field, filtered exactly as the spec sheet filters them ── */
  function range(min, max, step) {
    var out = [], n = Math.round((max - min) / step);
    for (var i = 0; i <= n; i++) out.push(String(Math.round((min + i * step) * 10) / 10));
    return out;
  }

  function valuesFor(key) {
    var t = tier(), vs = variantSpec();
    switch (key) {
      case 'hand': return t.hands;
      case 'lieAngle': return CFG.lieAngles;
      case 'loft': return RULES.lofts;
      case 'shaft': return t.shafts;
      case 'grip': return Object.keys(CFG.grips[putterType()] || {});
      case 'gripModel':
        if (!state.specs.gripBrand) return [];
        return ((CFG.grips[putterType()] || {})[state.specs.gripBrand] || []).map(function(m) {
          return { v: state.specs.gripBrand + ' ' + m, l: m };
        });
      case 'headcover': return t.headcovers;
      case 'faceMaterial': return t.faceMaterials;
      case 'sightLines': return CFG.sightLines;
      case 'faceAngle': return RULES.faceAngles;
      case 'paintColor': return CFG.paintColors.map(function(p) { return { v: p.n, l: p.n, c: p.c }; });
      case 'faceGrooves': return CFG.faceGrooves;
      case 'length': return range(vs.length[0], vs.length[1], 0.5);
      case 'weight': return range(vs.weight[0], vs.weight[1], 5);
      default: return [];
    }
  }

  /* ── Cascades ── */
  function applyPutterType() {
    var vs = variantSpec();
    var lengths = range(vs.length[0], vs.length[1], 0.5);
    var weights = range(vs.weight[0], vs.weight[1], 5);
    if (lengths.indexOf(state.specs.length) === -1) state.specs.length = lengths[Math.floor(lengths.length / 2)];
    if (weights.indexOf(state.specs.weight) === -1) state.specs.weight = weights[Math.floor(weights.length / 2)];
    /* Grips are filtered by putter style, not tier. A Regular grip does not
       exist on a broomstick, so the old answer has to go. */
    var brands = CFG.grips[putterType()] || {};
    if (!state.specs.gripBrand || !brands[state.specs.gripBrand]) {
      state.specs.gripBrand = null;
      state.specs.grip = null;
    }
  }

  function applyTier() {
    var t = tier();
    if (t.hands.indexOf(state.specs.hand) === -1) state.specs.hand = t.hands[0];
    if (state.specs.shaft && t.shafts.indexOf(state.specs.shaft) === -1) state.specs.shaft = null;
    if (state.specs.faceMaterial && t.faceMaterials.indexOf(state.specs.faceMaterial) === -1) state.specs.faceMaterial = null;
    if (state.specs.headcover && t.headcovers.indexOf(state.specs.headcover) === -1) state.specs.headcover = null;
    if (!isBespoke()) {
      state.specs.engraving = '';
      state.specs.sightLines = null;
      state.specs.paintColor = null;
      state.specs.faceGrooves = null;
      state.specs.faceAngle = RULES.defaults.faceAngle;
    }
    /* Dropping out of Bespoke removes a step. */
    if (steps().length && stepIndex() === 0 && state.step !== steps()[0].key) state.step = steps()[0].key;
    var keys = steps().map(function(s) { return s.key; });
    if (keys.indexOf(state.step) === -1) state.step = keys[keys.length - 1];
  }

  /* ── Controls ── */
  function fieldOpen(key, extraClass) {
    var label = RULES.fieldLabels[key];
    var req = RULES.required.indexOf(key) !== -1;
    return '<div class="igc-field' + (extraClass ? ' ' + extraClass : '') + '" data-field="' + key + '">' +
           '<div class="igc-label">' + esc(label) + (req ? ' <span class="igc-req">*</span>' : '') + '</div>';
  }

  function normalize(items) {
    return items.map(function(it) {
      return (typeof it === 'string') ? { v: it, l: it } : it;
    });
  }

  function cards(key) {
    var items = normalize(valuesFor(key));
    var sel = (key === 'grip') ? state.specs.gripBrand : state.specs[key];
    var html = '<div class="igc-cards">';
    items.forEach(function(it) {
      var label = RULES.units[key] ? withUnit(key, it.v) : it.l;
      html += '<button type="button" class="igc-card' + (sel === it.v ? ' active' : '') +
              '" data-spec="' + key + '" data-value="' + esc(it.v) + '">' +
              '<span class="igc-card-name">' + esc(label) + '</span></button>';
    });
    return html + '</div>';
  }

  function select(key, placeholder) {
    var items = normalize(valuesFor(key));
    var html = '<select class="igc-select" data-spec="' + key + '">';
    if (placeholder) html += '<option value=""' + (state.specs[key] ? '' : ' selected') + '>' + esc(placeholder) + '</option>';
    items.forEach(function(it) {
      var label = RULES.units[key] ? withUnit(key, it.v) : it.l;
      var target = (key === 'gripModel') ? state.specs.grip : state.specs[key];
      html += '<option value="' + esc(it.v) + '"' + (target === it.v ? ' selected' : '') + '>' + esc(label) + '</option>';
    });
    return html + '</select>';
  }

  function swatches(key) {
    var items = normalize(valuesFor(key));
    var html = '<div class="igc-swatches">';
    items.forEach(function(it) {
      var cls = it.c || CFG.faceMaterialColors[it.v] || 'ss-swatch-steel';
      html += '<div class="igc-swatch-item">' +
              '<button type="button" class="igc-swatch' + (state.specs[key] === it.v ? ' active' : '') +
              ' ' + esc(cls) + '" data-spec="' + key + '" data-value="' + esc(it.v) +
              '" title="' + esc(it.l) + '" aria-label="' + esc(it.l) + '"></button>' +
              '<span class="igc-swatch-label">' + esc(it.l) + '</span></div>';
    });
    return html + '</div>';
  }

  /* Slider, matching the spec sheet's live value and min/max rail labels. */
  function slider(key) {
    var vals = valuesFor(key);
    var min = parseFloat(vals[0]), max = parseFloat(vals[vals.length - 1]);
    var step = (key === 'length') ? 0.5 : 5;
    var cur = state.specs[key] != null ? state.specs[key] : vals[Math.floor(vals.length / 2)];
    return '<div class="igc-slider">' +
           '<div class="igc-slider-value" data-slider-value="' + key + '">' + esc(withUnit(key, cur)) + '</div>' +
           '<input type="range" class="igc-range" data-spec="' + key + '" min="' + min + '" max="' + max +
           '" step="' + step + '" value="' + esc(cur) + '">' +
           '<div class="igc-slider-rail"><span>' + esc(withUnit(key, vals[0])) + '</span>' +
           '<span>' + esc(withUnit(key, vals[vals.length - 1])) + '</span></div></div>';
  }

  function renderField(key) {
    /* Grip Model only appears once a brand is chosen, as on the spec sheet. */
    if (key === 'gripModel' && !state.specs.gripBrand) return '';

    var html = fieldOpen(key);
    switch (RULES.fieldControls[key]) {
      case 'cards': html += cards(key); break;
      case 'select': html += select(key, key === 'gripModel' ? 'Select ' + state.specs.gripBrand + ' model' : null); break;
      case 'swatches': html += swatches(key); break;
      case 'slider': html += slider(key); break;
      case 'text':
        html += '<input type="text" class="igc-input" data-spec="engraving" maxlength="40" ' +
                'placeholder="Your text here (max 40 characters)" value="' + esc(state.specs.engraving) + '">' +
                '<div class="igc-counter">' + state.specs.engraving.length + ' / 40</div>';
        break;
    }
    if (key === 'hand' && tier().hands.length === 1) {
      html += '<div class="igc-note">Left-hand builds are made in the Bespoke tier.</div>';
    }
    return html + '</div>';
  }

  /* ── Rendering ── */
  function renderTabs() {
    var list = steps(), idx = stepIndex();
    var html = '';
    list.forEach(function(s, i) {
      html += '<button type="button" class="igc-tab' + (s.key === state.step ? ' active' : '') +
              (i < idx ? ' done' : '') + '" data-igc-tab="' + s.key + '">' +
              '<span class="igc-tab-num">Step ' + (i + 1) + '</span>' +
              '<span class="igc-tab-name">' + esc(s.tabLabel || s.label) + '</span></button>';
    });
    var el = root.querySelector('[data-igc-tabs]');
    /* Up to three tabs share the row equally. Four (Bespoke) do not fit equal
       columns -- "Components" overran its quarter -- so they size to their text. */
    el.style.gridTemplateColumns = list.length > 3 ? '' : 'repeat(' + list.length + ', 1fr)';
    el.classList.toggle('igc-tabs--4', list.length > 3);
    el.innerHTML = html;
  }

  function renderBody() {
    var list = steps(), idx = stepIndex();
    var section = list[idx];
    var html = '';

    /* No step heading here: the active tab directly above already names the
       step, and "BASICS" under "BASICS" read as a repeat (Nakul, 2026-09-19). */
    /* The two priced Shopify options ARE the Basics step -- the spec sheet's
       tier and variant screens, folded into the page. */
    if (section.key === 'basics') {
      orderedOptions().forEach(function(name) {
        var oi = product.options.indexOf(name);
        var values = [];
        product.variants.forEach(function(v) {
          if (values.indexOf(v.options[oi]) === -1) values.push(v.options[oi]);
        });
        if (values.length <= 1) return;
        var isTier = name.toLowerCase() === 'customization level';

        /* Shopify hands these back in VARIANT order, and the Stock variant sits
           last, so the ladder rendered Custom/Premium/Bespoke/Stock. Nakul asked
           for it to ascend. Sorting by each value's cheapest variant is
           self-maintaining -- a new tier slots in at its own price with no list
           to update -- and stable, because it does not move when the buyer
           changes putter type. The control template does the same sort in
           Liquid (product-hero-rfb.liquid); Liquid cannot read this config, so
           the two are deliberate mirrors. */
        if (isTier) {
          var floor = {};
          values.forEach(function(val) {
            var lowest = null;
            product.variants.forEach(function(v) {
              if (v.options[oi] !== val) return;
              var c = Number(v.price);
              if (lowest === null || c < lowest) lowest = c;
            });
            floor[val] = lowest === null ? Infinity : lowest;
          });
          values.sort(function(a, b) { return floor[a] - floor[b]; });
        }

        html += '<div class="igc-field" data-field="opt-' + esc(name) + '">';
        html += '<div class="igc-label">' + esc(name) + ' <span class="igc-req">*</span></div>';
        html += '<div class="igc-cards ' + (isTier ? 'igc-cards-tier' : 'igc-cards-opt') + '">';
        values.forEach(function(v) {
          var cents = priceForOption(name, v);
          /* cents === null means no variant exists for this value alongside the
             current selections. Stock is Regular-only, so Counterbalanced,
             Armlock and Broomstick are unreachable while Stock is chosen. The
             card used to render clickable with no price; now it is disabled, so
             a combination that cannot be bought cannot be selected. */
          var reachable = cents !== null;
          html += '<button type="button" class="igc-card' +
                  (state.options[name] === v ? ' active' : '') +
                  (reachable ? '' : ' igc-card--unavailable') +
                  '" data-option="' + esc(name) + '" data-value="' + esc(v) + '"' +
                  (reachable ? '' : ' disabled aria-disabled="true"') + '>' +
                  '<span class="igc-card-name">' + esc(v) + '</span>';
          if (reachable) html += '<span class="igc-card-price">' + money(cents) + '</span>';
          html += '</button>';
        });
        html += '</div>';
        /* A trailing "(Right-hand only)" stays on one line; phones broke it at the hyphen. */
        if (isTier && tier().desc) html += '<div class="igc-note igc-note--tier">' + esc(tier().desc).replace(/\(([^)]+)\)$/, '<span class="igc-nowrap">($1)</span>') + '</div>';
        html += '</div>';
      });
    }

    tierFields(section).forEach(function(key) { html += renderField(key); });

    /* Notes closes the last step, where the spec sheet puts it. */
    if (idx === list.length - 1) {
      html += fieldOpen('notes') +
              '<textarea class="igc-input igc-textarea" data-spec="notes" rows="3" ' +
              'placeholder="Anything else our team should know about your build?">' +
              esc(state.specs.notes) + '</textarea></div>';
    }
    root.querySelector('[data-igc-body]').innerHTML = html;
  }

  function missing() {
    var only = tier().fieldsOnly || null;
    return RULES.required.filter(function(k) {
      /* Never block checkout on a question this tier does not ask. Stock would
         otherwise sit forever on "Still to choose: Shaft, Grip". */
      if (only && only.indexOf(k) === -1) return false;
      if (!isBespoke() && RULES.bespokeOnly.indexOf(k) !== -1) return false;
      return !state.specs[k];
    });
  }

  function render() {
    renderTabs();
    renderBody();

    var v = currentVariant();
    var priceEl = root.querySelector('[data-igc-price]');
    if (priceEl && v) priceEl.textContent = money(v.price);

    /* The hero price block is Liquid-rendered and updated, on the control
       template, by product-page.js keying off [data-tier-btn]. This template
       has no such buttons, so without this the headline sat at $799 while the
       buyer configured a $2,199 Bespoke Armlock. */
    if (v) {
      var heroPrice = document.querySelector('.ig-price-main');
      if (heroPrice) heroPrice.textContent = money(v.price);
      var heroTier = document.querySelector('.ig-price-tier');
      if (heroTier) heroTier.textContent = optionValue('customization level') || '';
      /* Stock is not custom-built: swap the price note with the level. */
      var isStock = /^stock$/i.test(optionValue('customization level') || '');
      Array.prototype.forEach.call(document.querySelectorAll('[data-price-note]'), function(el) {
        el.textContent = el.getAttribute(isStock ? 'data-note-stock' : 'data-note-default');
      });
      var idInput = root.querySelector('[data-igc-variant-id]');
      if (idInput) idInput.value = v.id;
    }

    var list = steps(), idx = stepIndex(), last = idx === list.length - 1;
    root.querySelector('[data-igc-prev]').hidden = idx === 0;
    var next = root.querySelector('[data-igc-next]');
    next.hidden = last;
    if (!last) next.textContent = 'Next: ' + (list[idx + 1].tabLabel || list[idx + 1].label);
    root.querySelector('[data-igc-actions]').hidden = !last;
    /* The build summary shows once, on the final step, directly above the buy
       buttons (Nakul + Nishant, 2026-09-19). On the earlier steps it was ~300px
       of choices made seconds ago, between the buyer and the next question; at
       the point of buying it is the one check before a built-to-spec order. */
    var summaryEl = root.querySelector('[data-igc-summary]');
    if (summaryEl) summaryEl.hidden = !last;

    if (last) {
      var gaps = missing();
      var buyable = !!currentVariant();
      root.querySelectorAll('[data-igc-submit]').forEach(function(b) {
        b.disabled = gaps.length > 0 || !buyable;
      });
      root.querySelector('[data-igc-hint]').textContent = gaps.length
        ? 'Still to choose: ' + gaps.map(function(k) { return RULES.fieldLabels[k]; }).join(', ')
        : '';
    }
    renderSummary();
    renderSticky();
  }

  function renderSummary() {
    var el = root.querySelector('[data-igc-summary]');
    if (!el) return;
    var rows = [];
    orderedOptions().forEach(function(name) {
      if (state.options[name]) rows.push([name, state.options[name]]);
    });
    var bespoke = isBespoke();
    var only = tier().fieldsOnly || null;
    Object.keys(RULES.propertyLabels).forEach(function(k) {
      if (!bespoke && RULES.bespokeOnly.indexOf(k) !== -1) return;
      /* A Stock order must not ship a Weight or Loft the buyer never chose. */
      if (only && only.indexOf(k) === -1) return;
      var v = state.specs[k];
      if (v) rows.push([RULES.fieldLabels[k] || RULES.propertyLabels[k], withUnit(k, v)]);
    });
    el.innerHTML = rows.map(function(r) {
      return '<div class="igc-sum-row"><span>' + esc(r[0]) + '</span><span>' + esc(r[1]) + '</span></div>';
    }).join('');
  }

  /* ── Cart ──
     Buy It Now cannot use the /cart/<id>:<qty>?checkout permalink: permalinks
     carry no line item properties, so every spec would be silently dropped.
     Both buttons POST, and Buy It Now redirects afterwards. */
  function buildProperties() {
    var props = {}, bespoke = isBespoke();
    Object.keys(RULES.propertyLabels).forEach(function(k) {
      if (!bespoke && RULES.bespokeOnly.indexOf(k) !== -1) return;
      var v = state.specs[k];
      if (v) props[RULES.propertyLabels[k]] = String(withUnit(k, v));
    });
    /* Keeps two identically-specced lines from merging into one line of qty 2,
       which would leave Nakul one spec sheet for two putters. Underscore-
       prefixed keys are private; the theme filters them out of cart display. */
    props._ig_build = 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    props._ig_source = 'pdp-configurator';
    return props;
  }

  function addToCart(thenCheckout) {
    var v = currentVariant();
    if (!v || missing().length) { render(); return; }
    var btns = Array.prototype.slice.call(root.querySelectorAll('[data-igc-submit]'));
    if (stickyBtn) btns.push(stickyBtn);
    btns.forEach(function(b) { b.disabled = true; });
    track('config_add_to_cart', { checkout_direct: !!thenCheckout, value: v.price / 100 });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: v.id, quantity: 1, properties: buildProperties() }] })
    })
      .then(function(res) { if (!res.ok) throw new Error('add failed'); return res.json(); })
      .then(function() {
        if (thenCheckout) { window.location.href = '/checkout'; return; }
        /* Go straight to the cart, as product-page.js does on the other PDPs
           (2026-09-04). A three-second "Added" line read as nothing happening;
           cart_type is "page", so the cart page is the confirmation. */
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        window.location.href = window.Shopify && window.Shopify.routes
          ? window.Shopify.routes.root + 'cart'
          : '/cart';
      })
      .catch(function() {
        btns.forEach(function(b) { b.disabled = false; });
        root.querySelector('[data-igc-hint]').textContent =
          'Something went wrong. Please try again, or call us on +1-516-260-0985.';
        /* A failure from the bottom bar lands in a hint that is off-screen. */
        root.querySelector('[data-igc-hint]').scrollIntoView({ block: 'center' });
      });
  }

  /* ── Bottom bar (snippets/sticky-atc.liquid, configurator: true) ──
     One button, two jobs. Until the build is complete and the buyer is on the
     review step it scrolls back to the build; after that it is Buy It Now,
     through addToCart() so the specs ride along as line item properties. */
  var sticky = document.getElementById('igcStickyAtc');
  var stickyBtn = sticky ? sticky.querySelector('[data-igc-sticky-btn]') : null;

  function readyToBuy() {
    var list = steps();
    return stepIndex() === list.length - 1 && !missing().length && !!currentVariant();
  }

  function renderSticky() {
    if (!sticky) return;
    var v = currentVariant();
    var priceEl = sticky.querySelector('[data-igc-sticky-price]');
    if (priceEl && v) priceEl.textContent = money(v.price);
    if (stickyBtn) stickyBtn.textContent = readyToBuy() ? 'Buy It Now' : 'Build Yours';
  }

  if (sticky && stickyBtn) {
    stickyBtn.addEventListener('click', function() {
      var ready = readyToBuy();
      track('config_sticky_click', { ready_to_buy: ready });
      if (ready) { addToCart(true); return; }
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    /* Show only once the buyer has scrolled PAST the whole configurator — the
       same rule the other PDPs apply to their Buy It Now button. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sticky.classList.toggle('sticky-atc--visible', scrolledPast);
        });
      }, { threshold: 0, rootMargin: '0px 0px -60px 0px' }).observe(root);
    }
  }

  /* ── Events ── */
  function goStep(key) {
    var keys = steps().map(function(s) { return s.key; });
    if (keys.indexOf(key) === -1) return;
    state.step = key;
    track('config_step_view', { step_name: key, step_index: keys.indexOf(key) + 1 });
    render();
  }

  root.addEventListener('click', function(e) {
    var t = e.target;

    var tab = t.closest('[data-igc-tab]');
    if (tab) { goStep(tab.getAttribute('data-igc-tab')); return; }

    var opt = t.closest('[data-option]');
    if (opt) {
      state.options[opt.getAttribute('data-option')] = opt.getAttribute('data-value');
      snapToRealVariant(opt.getAttribute('data-option'));
      applyPutterType();
      applyTier();
      track('config_field_set', { field: opt.getAttribute('data-option'), value: opt.getAttribute('data-value') });
      render();
      return;
    }

    var card = t.closest('[data-spec][data-value]');
    if (card) {
      var key = card.getAttribute('data-spec'), val = card.getAttribute('data-value');
      if (key === 'grip') { state.specs.gripBrand = val; state.specs.grip = null; }
      else state.specs[key] = val;
      track('config_field_set', { field: key, value: val });
      render();
      return;
    }

    var list = steps(), idx = stepIndex();
    if (t.closest('[data-igc-next]') && idx < list.length - 1) { goStep(list[idx + 1].key); return; }
    if (t.closest('[data-igc-prev]') && idx > 0) { goStep(list[idx - 1].key); return; }
    if (t.closest('[data-igc-buynow]')) { addToCart(true); return; }
    if (t.closest('[data-igc-addcart]')) { addToCart(false); return; }
  });

  root.addEventListener('change', function(e) {
    var el = e.target;
    if (!el.hasAttribute || !el.hasAttribute('data-spec')) return;
    if (el.type === 'range') return;                 /* handled on input */
    var key = el.getAttribute('data-spec');
    state.specs[key === 'gripModel' ? 'grip' : key] = el.value || null;
    track('config_field_set', { field: key, value: el.value });
    render();
  });

  /* Sliders and text inputs update in place — a full re-render would drop
     focus mid-drag or mid-word. */
  root.addEventListener('input', function(e) {
    var el = e.target;
    if (!el.hasAttribute || !el.hasAttribute('data-spec')) return;
    var key = el.getAttribute('data-spec');
    if (el.type === 'range') {
      state.specs[key] = el.value;
      var out = root.querySelector('[data-slider-value="' + key + '"]');
      if (out) out.textContent = withUnit(key, el.value);
    } else if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
      state.specs[key] = el.value;
      if (key === 'engraving') {
        var c = el.parentNode.querySelector('.igc-counter');
        if (c) c.textContent = el.value.length + ' / 40';
      }
    } else return;
    renderSummary();
  });

  /* ── Init ── */
  /* Start on the variant named in the link (?variant=…) when there is one. The
     catalog ad lands on the $549 Stock variant; starting on variants[0] showed
     Custom $799 instead (2026-09-24). */
  var urlVariant = (location.search.match(/[?&]variant=(\d+)/) || [])[1];
  var start = product.variants.filter(function(v) { return String(v.id) === urlVariant; })[0] ||
              product.variants[0];
  product.options.forEach(function(name, i) {
    state.options[name] = start ? start.options[i] : null;
  });
  applyPutterType();
  applyTier();
  track('config_step_view', { step_name: 'basics', step_index: 1 });
  render();
})();
